import React, { FC, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faAngleDown, faShare, faTrash, faTrashUndo } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import notification from "antd/es/notification";
import { omit } from "lodash-es";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { Button, Popconfirm, SearchField } from "@mapmycustomers/ui";

import CreatorFilter from "@app/component/CreatorFilter/CreatorFilter";
import RoundButton from "@app/component/RoundButton";
import { getCurrentUserId } from "@app/store/iam";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { ResourceFieldName } from "@app/util/fieldModel/ResourceFieldModel";
import { userDisplayName } from "@app/util/formatters";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import ResourceContentType, { getResourceContentTypeName } from "../../enum/ResourceContentType";
import { getResourcesViewState, isResourceOrFolderSharingAllowed } from "../../store";
import { bulkDeleteResources, bulkEditResources, fetchResources } from "../../store/actions";
import getResourceContentTypeFilter, {
  getResourceContentTypeFromFilter,
} from "../../utils/getResourceContentTypeFilter";
import ShareModal from "../ShareModal";

import ListOrderSelect from "./ListOrderSelect";
import messages from "./messages";
import styles from "./ToolBar.module.scss";

const CLEAR_OPTION_VALUE = "clear";

type ToolBarProps = {
  isSharedFolder: boolean;
  isTrash: boolean;
  selectedResourceIds: Resource["id"][];
} & typeof mapDispatchToProps &
  ReturnType<typeof mapStateToProps>;

const ToolBar: FC<ToolBarProps> = ({
  currentUserId,
  isSharedFolder,
  isTrash,
  onBulkDeleteResources,
  onBulkEditResources,
  onFetchResources,
  selectedResourceIds,
  sharingAllowed,
  users,
  viewState,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const { filter, range, sort } = viewState;

  const firstPageRange = useMemo(
    () => ({ endRow: range.endRow - range.startRow, startRow: 0 }),
    [range.endRow, range.startRow]
  );

  const [shareModal, showShareModal, hideShareModal] = useBoolean();

  const resourceTypeItems: MenuProps["items"] = useMemo(
    () => [
      {
        className: styles.clearMenuItem,
        key: CLEAR_OPTION_VALUE,
        label: intl.formatMessage(messages.clear),
      },
      ...Object.values(ResourceContentType).map((type) => ({
        key: type,
        label: getResourceContentTypeName(intl, type),
      })),
    ],
    [intl]
  );

  const selectedUserIds: User["id"][] = useMemo(() => {
    const condition = filter[ResourceFieldName.OWNER];
    if (!condition || !isSimpleCondition(condition)) {
      return [];
    }

    if (condition.operator === FilterOperator.CONTAINS) {
      return condition.value;
    }

    return [];
  }, [filter]);

  const sharedWithUserIds: User["id"][] = useMemo(() => {
    const condition = filter[ResourceFieldName.SHARED_WITH];

    if (!condition || !isSimpleCondition(condition)) {
      return [];
    }

    if (condition.operator === FilterOperator.CONTAINS) {
      return condition.value;
    }

    return [];
  }, [filter]);

  const [myFilesActive, sharedWithMeActive] = useMemo(
    () => [
      selectedUserIds.length === 1 && selectedUserIds[0] === currentUserId,
      sharedWithUserIds.length === 1 &&
        sharedWithUserIds[0] === currentUserId &&
        !selectedUserIds.length,
    ],
    [currentUserId, selectedUserIds, sharedWithUserIds]
  );

  const selectedResourceType = useMemo(
    () => (viewState.filter ? getResourceContentTypeFromFilter(viewState.filter) : undefined),
    [viewState.filter]
  );

  const handleSort = useCallback(
    (field: IField, order: SortOrder) => {
      onFetchResources({ sort: [{ field, order }] });
    },
    [onFetchResources]
  );

  const handleMenuClick = useCallback(
    (item: MenuInfo) => {
      const key = item.key as ResourceContentType | typeof CLEAR_OPTION_VALUE;
      onFetchResources({
        filter:
          key === CLEAR_OPTION_VALUE
            ? omit(filter, ResourceFieldName.TYPE, ResourceFieldName.URL)
            : { ...filter, ...getResourceContentTypeFilter(key) },
        range: firstPageRange,
      });
      analytics.clicked([
        "Resource Type",
        key === "clear" ? "Clear" : getResourceContentTypeName(intl, key),
      ]);
    },
    [onFetchResources, filter, firstPageRange, analytics, intl]
  );

  const handleFilterMyFiles = useCallback(() => {
    analytics.clicked(["My Files"]);
    onFetchResources({
      filter: myFilesActive
        ? omit(filter, ResourceFieldName.OWNER)
        : {
            ...omit(filter, ResourceFieldName.SHARED_WITH),
            [ResourceFieldName.OWNER]: {
              operator: FilterOperator.CONTAINS,
              value: [currentUserId],
            },
          },
      range: firstPageRange,
    });
  }, [analytics, onFetchResources, myFilesActive, filter, currentUserId, firstPageRange]);

  const handleFilterSharedWithMe = useCallback(() => {
    analytics.clicked(["Shared With Me"]);
    onFetchResources({
      filter: sharedWithMeActive
        ? omit(filter, ResourceFieldName.OWNER, ResourceFieldName.SHARED_WITH)
        : {
            ...filter,
            [ResourceFieldName.OWNER]: {
              operator: FilterOperator.NOT_EQUAL,
              value: currentUserId,
            },
            [ResourceFieldName.SHARED_WITH]: {
              operator: FilterOperator.CONTAINS,
              value: [currentUserId],
            },
          },
      range: firstPageRange,
    });
  }, [analytics, onFetchResources, sharedWithMeActive, filter, currentUserId, firstPageRange]);

  const handleSearch = useDebouncedCallback(
    [
      (value: string) => {
        onFetchResources({
          filter: {
            ...filter,
            [ResourceFieldName.NAME]: {
              operator: FilterOperator.CONTAINS,
              value,
            },
          },
          range: firstPageRange,
        });
        analytics.completed(["Search Successful"]);
      },
      500,
    ],
    [onFetchResources, firstPageRange]
  );

  const handleDelete = useCallback(() => {
    onBulkDeleteResources({
      callback: () => {
        if (isTrash) {
          analytics.completed(["Trash", "Bulk Delete Forever"]);
        }
      },
      ids: selectedResourceIds,
      isTrash,
    });
  }, [analytics, isTrash, onBulkDeleteResources, selectedResourceIds]);

  const handleShare = useCallback(
    (userIds: User["id"][]) => {
      onBulkEditResources({
        callback: () => {
          const sharedWith = userIds.filter((id) => currentUserId !== id);
          const user =
            sharedWith.length === 1 ? users.find(({ id }) => sharedWith.includes(id)) : undefined;

          notification.success({
            message: intl.formatMessage(messages.resourceShareSuccess, {
              count: sharedWith.length,
              resourceCount: selectedResourceIds.length,
              userName: user ? userDisplayName(user) : undefined,
            }),
          });
          analytics.completed(["Share Resources", "Share Successful"]);
        },
        resources: selectedResourceIds.map((id) => ({ id, user: { id: currentUserId }, userIds })),
      });
    },
    [analytics, currentUserId, intl, onBulkEditResources, selectedResourceIds, users]
  );

  const handleRestore = useCallback(() => {
    onBulkEditResources({
      callback: () => {
        notification.success({
          message: intl.formatMessage(messages.restoreSuccess, {
            count: selectedResourceIds.length,
          }),
        });
      },
      resources: selectedResourceIds.map((id) => ({ id, folder: null })),
    });
  }, [intl, onBulkEditResources, selectedResourceIds]);

  const handleChangeOwnerFilter = useCallback(
    (userIds: User["id"][]) => {
      onFetchResources({
        filter: {
          [ResourceFieldName.OWNER]: { operator: FilterOperator.CONTAINS, value: userIds },
          [ResourceFieldName.SHARED_WITH]: {
            operator: FilterOperator.CONTAINS,
            value: [currentUserId],
          },
        },
      });
    },
    [currentUserId, onFetchResources]
  );

  return (
    <div className={styles.container}>
      <div className={styles.left}>
        <Dropdown
          menu={{
            items: resourceTypeItems,
            onClick: handleMenuClick,
            selectedKeys: selectedResourceType ? [selectedResourceType] : [],
          }}
          trigger={["click"]}
        >
          <RoundButton
            active={!!selectedResourceType}
            rightIcon={<FontAwesomeIcon icon={faAngleDown} />}
          >
            {selectedResourceType
              ? getResourceContentTypeName(intl, selectedResourceType)
              : intl.formatMessage(messages.resourceType)}
          </RoundButton>
        </Dropdown>
        {!isTrash && !isSharedFolder && (
          <>
            <CreatorFilter
              label={intl.formatMessage(messages.owner)}
              onChange={handleChangeOwnerFilter}
              selectedUserIds={selectedUserIds}
            />
            <RoundButton active={myFilesActive} onClick={handleFilterMyFiles}>
              {intl.formatMessage(messages.myFiles)}
            </RoundButton>
            <RoundButton active={sharedWithMeActive} onClick={handleFilterSharedWithMe}>
              {intl.formatMessage(messages.sharedWithMe)}
            </RoundButton>
          </>
        )}
      </div>
      <div className={styles.right}>
        {selectedResourceIds.length > 0 && (
          <>
            {isTrash && (
              <Button icon={<FontAwesomeIcon icon={faTrashUndo} />} onClick={handleRestore}>
                {intl.formatMessage(messages.restore)}
              </Button>
            )}
            <Popconfirm
              danger
              description={intl.formatMessage(messages.deleteConfirm, {
                isTrash,
                multi: selectedResourceIds.length > 1,
              })}
              okText={intl.formatMessage(messages.delete, { isTrash })}
              onConfirm={handleDelete}
              overlayClassName={styles.popconfirm}
              placement="right"
              trigger={["click"]}
              zIndex={1050}
            >
              <Button danger icon={<FontAwesomeIcon icon={faTrash} />} type="primary">
                {intl.formatMessage(messages.delete, { isTrash })}
              </Button>
            </Popconfirm>
            {!isTrash && sharingAllowed && (
              <Button icon={<FontAwesomeIcon icon={faShare} />} onClick={showShareModal}>
                {intl.formatMessage(messages.share)}
              </Button>
            )}
          </>
        )}
        <ListOrderSelect onChange={handleSort} sort={sort} />
        <SearchField onChange={handleSearch} placeholder={intl.formatMessage(messages.search)} />
      </div>
      <ShareModal
        onChangeUserIds={handleShare}
        onHide={hideShareModal}
        open={shareModal}
        title={intl.formatMessage(messages.shareTitle, {
          count: selectedResourceIds.length,
        })}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  sharingAllowed: isResourceOrFolderSharingAllowed(state),
  users: getUsers(state),
  viewState: getResourcesViewState(state),
});

const mapDispatchToProps = {
  onBulkDeleteResources: bulkDeleteResources.request,
  onBulkEditResources: bulkEditResources.request,
  onFetchResources: fetchResources.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ToolBar);
